import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  db, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  googleProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from '../firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);

  // Initialize user data in Firestore
  const initializeUserData = async (user) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create new user document
        await setDoc(userRef, {
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          role: 'user', // Default role for new users
          createdAt: new Date(),
          lastLogin: new Date(),
          emailVerified: false
        });

        // Initialize user progress
        await setDoc(doc(db, 'userProgress', user.uid), {
          points: 0,
          achievements: [],
          questions_asked: 0,
          questions_answered: 0,
          materials_uploaded: 0,
          materials_downloaded: 0,
          consecutive_logins: 0,
          last_login: new Date(),
          updatedAt: new Date()
        });

        // Initialize leaderboard entry
        await setDoc(doc(db, 'leaderboard', user.uid), {
          score: 0,
          updatedAt: new Date()
        });
        
        return { ...user, role: 'user' };
      } else {
        // Update last login
        await updateDoc(userRef, {
          lastLogin: new Date()
        });
        
        return { ...user, role: userDoc.data().role || 'user' };
      }
    } catch (err) {
      console.error('Error initializing user data:', err);
      setError(err.message);
      return user;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      try {
        if (user) {
          const userWithRole = await initializeUserData(user);
          setCurrentUser(userWithRole);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err.message);
        setCurrentUser(user);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signup = async (email, password, displayName) => {
    try {
      setError('');
      setVerificationSent(false);
      
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Send email verification
      await sendEmailVerification(userCredential.user);
      setVerificationSent(true);

      // Initialize user data and get user with role
      const userWithRole = await initializeUserData(userCredential.user);
      return userWithRole;
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        await sendEmailVerification(userCredential.user);
        setVerificationSent(true);
        throw new Error('Please verify your email before logging in. A new verification email has been sent.');
      }
      
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError('');
      // Configure Google provider
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });
      // Use popup instead of redirect for better control
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user) {
        await initializeUserData(result.user);
        setCurrentUser(result.user);
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError('');
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message);
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError('');
      if (currentUser && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser);
        setVerificationSent(true);
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setError(error.message);
      throw error;
    }
  };

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail,
    verificationSent,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 